import axios from "axios";
import Cookies from "js-cookie";
import fileDownload from "js-file-download";
import endpoints from "../utils/endpoints";
import { accessToken } from "../utils/token";

const getAllPurchases = async (params: any) => {
  const url = endpoints.PURCHASE;
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
    params: params,
  });
  return response;
}

const exportPurchases = async (params: any) => {
  const url = endpoints.PURCHASE + "/export";
  const startDate = params?.startDate || "all";
  const endDate = params?.endDate || "all";

  await axios.get(url, {
    headers: {
      Authorization: accessToken,
      ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    params,
    responseType: "arraybuffer",
  }).then((res) => {
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    fileDownload(blob, `Purchases_${startDate}_to_${endDate}.xlsx`);
  });
};

const viewPurchaseDetail = async (id: any) => {
  if (parseInt(id)) {
    const url = endpoints.PURCHASE + "/" + id;
    const { data: response } = await axios.get(url, {
      headers: {
        Authorization: accessToken,
      },
    });
    return response;
  }
};

const createPurchase = async (params: any) => {
  const url = endpoints.PURCHASE;
  const formData = new FormData();
  const fileFieldNames = ["invoice", "invoice_file", "ktp", "npwp"];

  Object.entries(params).forEach(([key, value]) => {
    if (key === "items") {
      formData.append(key, JSON.stringify(value));
    } else if (fileFieldNames.includes(key) && value instanceof File) {
      formData.append(key === "invoice_file" ? "invoice" : key, value);
    } else if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  const { data: response } = await axios.post(url, formData, {
    headers: {
      Authorization: accessToken,
      "Content-Type": "multipart/form-data",
    },
  });

  return response;
};

const deletePurchase = async (id: number) => {
  const url = endpoints.PURCHASE + "/" + id;

  const { data: response } = await axios.delete(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const updatePurchase = async (params: any) => {
  const url = endpoints.PURCHASE + "/" + params.id;

  const { data: response } = await axios.patch(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const downloadPurchase = async (purchaseId: number) => {
  const url = endpoints.PURCHASE + "/download/" + purchaseId;
  await axios.get(url, {
    headers: {
      Authorization: accessToken,
      ContentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    responseType: 'arraybuffer',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const filename = contentDisposition?.split('filename=')[1] || `${purchaseId}.pdf`;
    var blob = new Blob([res.data], {
      type: 'application/pdf'
    });
    fileDownload(blob, filename);
  });
};

const getPurchaseFiles = async (purchaseId: number) => {
  const url = `${endpoints.PURCHASE}/files/${purchaseId}`;
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const downloadPurchaseFile = async (fileId: number) => {
  const url = `${endpoints.PURCHASE}/download-file/${fileId}`;
  await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
    responseType: 'blob',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const contentType = res.headers['content-type'] || 'application/octet-stream';
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `File-${fileId}`;
    const blob = new Blob([res.data], { type: contentType });
    fileDownload(blob, filename);
  });
};

const downloadPaymentProof = async (fileId: number) => {
  const url = `${endpoints.PURCHASE}/download-payment-proof/${fileId}`;
  await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
    responseType: 'blob',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const contentType = res.headers['content-type'] || 'application/octet-stream';
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || `File-${fileId}`;
    const blob = new Blob([res.data], { type: contentType });
    fileDownload(blob, filename);
  });
};

const downloadBAST = async (purchaseId: number, purchaseNo: string) => {
  const url = `${endpoints.PURCHASE}/download-bast/${purchaseId}`;
  await axios.get(url, {
    headers: {
      Authorization: accessToken,
      ContentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    },
    responseType: "blob",
  }).then((res) => {
    const contentDisposition = res.headers["content-disposition"];
    const contentType = res.headers["content-type"] || "application/octet-stream";
    const defaultFilename = `BAST - ${purchaseNo}.docx`;
    const filename = contentDisposition?.split("filename=")[1]?.replace(/"/g, "") || defaultFilename;
    const blob = new Blob([res.data], { type: contentType });
    fileDownload(blob, filename);
  });
};

const uploadPurchaseFile = async (id: number, file: File, type: string = "payment proof") => {
  const url = `${endpoints.PURCHASE}/upload-file`;
  const formData = new FormData();
  formData.append("purchase_file", file);
  formData.append("purchase_id", id.toString());
  formData.append("type", type);
  // Add employee data from cookies
  const user = JSON.parse(Cookies.get("USER") || "{}");
  formData.append("created_by", user.id);

  const { data: response } = await axios.post(url, formData, {
    headers: {
      Authorization: accessToken,
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

const acceptPurchase = async (params: any) => {
  const url = endpoints.PURCHASE + "/accept/" + params.id;

  const { data: response } = await axios.post(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const declinePurchase = async (params: any) => {
  const url = endpoints.PURCHASE + "/decline/" + params.id;

  const { data: response } = await axios.post(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const service = {
  getAllPurchases,
  exportPurchases,
  viewPurchaseDetail,
  createPurchase,
  deletePurchase,
  updatePurchase,
  downloadPurchase,
  uploadPurchaseFile,
  downloadPurchaseFile,
  downloadPaymentProof,
  downloadBAST,
  getPurchaseFiles,
  acceptPurchase,
  declinePurchase,
};

export default service;
